import { PERMISSIONS } from '@/lib/permissions'

/**
 * What a request type means, in words a checker reads.
 *
 * The engine's naming rule is mechanical — an operation authorised by `loans.disburse` is
 * countersigned by `loans.disburse.approve` — so the request type on a row *is* the maker
 * permission, and everything below is derived from it rather than invented alongside it.
 *
 * The keys are taken from the generated permission catalogue, so a permission renamed on the
 * server breaks this file at compile time instead of quietly dropping a row back to its raw
 * dotted code.
 */

/**
 * The module a request belongs to, as a plain string.
 *
 * Deliberately not a closed union. The checker permissions include `sync.device.revoke.approve`,
 * and the catalogue also declares `reports`, `aml` and `ops` — a union written from the modules
 * that happen to ship a dual-control policy today cannot represent a request type the permission
 * list already admits, and would fail to type the moment one is added. An unrecognised module
 * renders under its own name.
 */
export const MODULE_LABELS: Record<string, string> = {
  [PERMISSIONS.moduleNames.admin]: 'Administration',
  [PERMISSIONS.moduleNames.aml]: 'Financial crime',
  [PERMISSIONS.moduleNames.customers]: 'Customers',
  [PERMISSIONS.moduleNames.groups]: 'Groups',
  [PERMISSIONS.moduleNames.ledger]: 'Ledger',
  [PERMISSIONS.moduleNames.loans]: 'Loans',
  [PERMISSIONS.moduleNames.notifications]: 'Notifications',
  [PERMISSIONS.moduleNames.ops]: 'Operations',
  [PERMISSIONS.moduleNames.reports]: 'Reports',
  [PERMISSIONS.moduleNames.savings]: 'Savings',
  [PERMISSIONS.moduleNames.sync]: 'Field sync',
  [PERMISSIONS.moduleNames.teller]: 'Tellering',
}

/**
 * The operations that ship under dual control, from the Access module's seeded policy set, plus
 * the one further request type the permission catalogue admits (`sync.device.revoke`).
 *
 * A request type absent from this table is still shown — the policy set is configuration and an
 * institution may add to it — it simply reads as its own code.
 */
export const REQUEST_TYPE_LABELS: Record<string, string> = {
  [PERMISSIONS.admin.userCreate]: 'New staff account',
  [PERMISSIONS.admin.userUpdate]: 'Change to a staff account',
  [PERMISSIONS.admin.userUnlock]: 'Unlock a staff account',
  [PERMISSIONS.admin.roleManage]: 'Role definition',
  [PERMISSIONS.admin.roleAssign]: 'Role assignment',
  [PERMISSIONS.admin.mfaReset]: 'Authenticator reset',
  [PERMISSIONS.admin.branchManage]: 'Branch record',
  [PERMISSIONS.admin.parameterManage]: 'System parameter',
  [PERMISSIONS.admin.approvalPolicyManage]: 'Approval policy',
  [PERMISSIONS.admin.sodOverride]: 'Segregation-of-duties override',

  [PERMISSIONS.customers.kycApprove]: 'KYC approval',
  [PERMISSIONS.customers.blacklist]: 'Customer blacklisting',
  [PERMISSIONS.customers.riskOverride]: 'Customer risk override',

  [PERMISSIONS.groups.create]: 'New group',
  [PERMISSIONS.groups.leaderChange]: 'Group leader change',
  [PERMISSIONS.groups.officerAssign]: 'Loan officer assignment',
  [PERMISSIONS.groups.locationManage]: 'Meeting location and geofence',
  [PERMISSIONS.groups.healthConfig]: 'Group health configuration',

  // The credit decision itself, which is separate from disbursement — three eyes on every loan.
  // It ships with no seeded policy, but it is an IApprovableCommand and the catalogue names
  // `loans.approve.approve`, so an institution that adds the policy gets a row here.
  [PERMISSIONS.loans.approve]: 'Credit approval',
  [PERMISSIONS.loans.disburse]: 'Loan disbursement',
  [PERMISSIONS.loans.repaymentReverse]: 'Repayment reversal',
  [PERMISSIONS.loans.penaltyWaive]: 'Penalty waiver',
  [PERMISSIONS.loans.restructure]: 'Loan restructure',
  [PERMISSIONS.loans.writeOff]: 'Loan write-off',
  [PERMISSIONS.loans.guaranteeOffset]: 'Security-deposit offset',
  [PERMISSIONS.loans.classificationOverride]: 'Classification override',
  [PERMISSIONS.loans.classificationConfig]: 'Classification scheme',
  [PERMISSIONS.loans.productManage]: 'Loan product',

  [PERMISSIONS.savings.withdrawalPost]: 'Savings withdrawal',
  [PERMISSIONS.savings.close]: 'Savings account closure',
  [PERMISSIONS.savings.productManage]: 'Savings product',

  [PERMISSIONS.teller.vaultTransfer]: 'Vault transfer',
  [PERMISSIONS.teller.limitOverride]: 'Teller limit override',
  [PERMISSIONS.teller.eodSubmit]: 'End-of-day till submission',

  [PERMISSIONS.ledger.journalManual]: 'Manual journal',
  [PERMISSIONS.ledger.journalReverse]: 'Journal reversal',
  [PERMISSIONS.ledger.periodReopen]: 'Accounting period reopen',
  [PERMISSIONS.ledger.accountManage]: 'Ledger account',

  [PERMISSIONS.notifications.templateManage]: 'Notification template',
  [PERMISSIONS.notifications.senderManage]: 'Notification sender identity',

  [PERMISSIONS.sync.deviceRevoke]: 'Field device revocation',
}

/**
 * The requests where countersigning moves money out of the institution or forgives a debt.
 *
 * Approving one of these is not undoable by another click, so the confirmation asks for a typed
 * word rather than a press. The criterion is applied literally rather than by feel:
 *
 *   · money leaves — disbursement, savings withdrawal, account closure (the whole balance is
 *     refunded), vault transfer, a manual journal or a journal reversal;
 *   · a debt is reduced or forgiven — write-off, penalty waiver, restructure, a repayment
 *     reversal (which takes back a receipt the customer was given), a security-deposit offset.
 *
 * `savings.withdrawal.post` is on the list and is the one that will dominate a real inbox: it
 * ships with no threshold, so *every* withdrawal is countersigned until the institution sets one.
 * Being the most frequent is not a reason to make it the least deliberate.
 */
export const IRREVERSIBLE_REQUEST_TYPES: ReadonlySet<string> = new Set<string>([
  PERMISSIONS.loans.disburse,
  PERMISSIONS.loans.repaymentReverse,
  PERMISSIONS.loans.penaltyWaive,
  PERMISSIONS.loans.restructure,
  PERMISSIONS.loans.writeOff,
  PERMISSIONS.loans.guaranteeOffset,
  PERMISSIONS.savings.withdrawalPost,
  PERMISSIONS.savings.close,
  PERMISSIONS.teller.vaultTransfer,
  PERMISSIONS.ledger.journalManual,
  PERMISSIONS.ledger.journalReverse,
])

/** The word typed to unlock an irreversible approval. */
export const IRREVERSIBLE_CONFIRMATION = 'APPROVE'

/**
 * The permission that countersigns an operation: the operation's own code plus `.approve`. This is
 * the engine's rule (`ApprovalPermissions.CheckerPermissionFor`), not a lookup table, which is why
 * adding a dual-controlled operation never involves inventing a permission name.
 */
export const checkerPermissionFor = (requestType: string) => `${requestType}.approve`

/** The inverse, used to read a checker's own capabilities off their permission list. */
export const APPROVE_SUFFIX = '.approve'

/**
 * Every permission code the generated catalogue declares, as a flat set.
 *
 * `moduleNames` is excluded: it holds module prefixes (`loans`, `admin`), not permissions.
 */
const PERMISSION_CODES: ReadonlySet<string> = new Set(
  Object.entries(PERMISSIONS)
    .filter(([group]) => group !== 'moduleNames')
    .flatMap(([, codes]) => Object.values(codes) as string[]),
)

/**
 * Whether a dotted code is a permission this build knows — and therefore whether it can be a
 * request type at all.
 *
 * This exists because the `.approve` naming rule is not injective. Two *maker* permissions end in
 * `.approve` themselves — `customers.kyc.approve` (approving a customer's KYC file) and
 * `loans.approve` (the credit decision) — and the catalogue names their checker halves
 * `customers.kyc.approve.approve` and `loans.approve.approve` to say so. Stripping the suffix off
 * every held permission that ends in it therefore produces two codes that are not request types:
 * `customers.kyc` and `loans`. A KYC officer or a credit officer would otherwise be told they may
 * countersign an operation named `loans`, which does not exist.
 *
 * A request type is always a permission code — `IApprovableCommand.ApprovalRequestType` returns a
 * permission constant, and the same constant gates the maker's own call — so membership of the
 * catalogue is the exact test, not an approximation of one. The catalogue is generated from the
 * server's and CI fails on a diff, so it cannot drift silently.
 */
export const isPermissionCode = (code: string) => PERMISSION_CODES.has(code)

export interface RequestTypeDescriptor {
  /** The raw dotted code, always shown somewhere: it is what an operator quotes down a phone. */
  code: string
  module: string
  moduleLabel: string
  /** The operation in words, or the code again when it is not one this build knows. */
  label: string
  /** False for a request type absent from {@link REQUEST_TYPE_LABELS}. */
  isKnown: boolean
  checkerPermission: string
  isIrreversible: boolean
}

export function describeRequestType(requestType: string): RequestTypeDescriptor {
  const module = requestType.split('.')[0] ?? requestType
  const label = REQUEST_TYPE_LABELS[requestType]

  return {
    code: requestType,
    module,
    moduleLabel: MODULE_LABELS[module] ?? module,
    label: label ?? requestType,
    isKnown: label !== undefined,
    checkerPermission: checkerPermissionFor(requestType),
    isIrreversible: IRREVERSIBLE_REQUEST_TYPES.has(requestType),
  }
}
